import { CdkAccordionItem } from '@angular/cdk/accordion';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Account } from '@app/core/api-azure-functions/dataverse-functions';
import { User } from '@app/core/models/auth-user';
import { ContentfulModel } from '@app/core/models/contentful.model';
import { Notification, UserDataModel } from '@app/core/models/UserDataModel';
import { PhonePipe } from '@app/shared/pipes/phone.pipe';
import {
  ContentfulActions,
  GlobalActions,
  TwilioActions,
  UserDataActions,
} from '@app/store/actions';
import { AppState } from '@app/store/app.state';
import {
  AuthSelectors,
  ContentfulSelectors,
  CorporateSelectors,
  TwilioSelectors,
  UserDataSelectors,
} from '@app/store/selectors';
import { AccountInfo } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-account-information',
  templateUrl: './account-information.component.html',
  styleUrls: ['./account-information.component.scss'],
  providers: [PhonePipe],
})
export class AccountInformationComponent implements OnInit, AfterViewInit {
  subscriptions: Subscription = new Subscription();
  fragment: string;
  isWhatsNewShowing: boolean;
  user: UserDataModel;
  notificationSettingForm: FormGroup;
  accountSettingForm: FormGroup;

  corporateAccount$: Observable<Account> = this.store.select(
    CorporateSelectors.getCorporateAccount
  );
  user$: Observable<User> = this.store.select(AuthSelectors.getUser);
  userinfo$ = this.store.select(UserDataSelectors.getUserInfo);
  whatsNew$: Observable<ContentfulModel> = this.store.select(
    ContentfulSelectors.getWhatsNew
  );
  termsAndConditions$: Observable<ContentfulModel> = this.store.select(
    ContentfulSelectors.getTermsAndConditions
  );
  privacyPolicy$: Observable<ContentfulModel> = this.store.select(
    ContentfulSelectors.getPrivacyPolicy
  );
  isPhoneValidationSent$ = this.store.select(
    TwilioSelectors.selectIsPhoneValidationSent
  );
  isPhoneValidated$: Observable<boolean> = this.store.select(
    TwilioSelectors.selectIsPhoneValidated
  );
  twilioError$ = this.store.select(TwilioSelectors.selectError);
  isNotificationEnable: boolean;

  @ViewChild('accordionItem2', { static: false })
  accordionItem2: CdkAccordionItem;

  constructor(
    private store: Store<AppState>,
    public route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private fbt: FormBuilder
  ) {
    this.accountSettingForm = this.fb.group({
      name: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern(/^\+[1-9]{1}[0-9]{3,14}$/)],
      ],
      isPhoneValidated: [{ value: false }],
      otp: ['', [Validators.minLength(4), Validators.maxLength(6)]],
      submit: [''],
    });

    this.notificationSettingForm = this.fbt.group({
      email: [false],
      sms: [false],
    });
  }

  ngOnInit(): void {
    this.route.fragment.subscribe(fragment => (this.fragment = fragment));
    this.isWhatsNewShowing = this.route.snapshot.params.whatsNewShowing;
    this.isNotificationEnable = true;

    if (this.route.snapshot.params.whatsNewShowing) {
      this.markAsSeen();
    }
    this.getUserInfo();

    this.subscriptions.add(
      this.isPhoneValidated$.subscribe((isPhoneValidated: boolean) => {
        if (isPhoneValidated && this.user.adObjectId) {
          // If the phone is validated, update the user's isPhoneValidated status in the API
          this.store.dispatch(GlobalActions.showSpinner());
          this.store.dispatch(
            UserDataActions.userUpdateAcccountSettingsById({
              user: {
                adObjectId: this.user.adObjectId,
                phoneNumber: this.accountSettingForm.get('phoneNumber').value,
                isPhoneValidated: isPhoneValidated,
              },
            })
          );
          this.store.dispatch(GlobalActions.hideSpinner());
        }
      })
    );
  }

  ngAfterViewInit(): void {
    // document.querySelector('#' + this.fragment).scrollIntoView();
    this.route.queryParams.subscribe(params => {
      const activatePanel = params.active;

      if (activatePanel === '1' && this.accordionItem2)
        this.accordionItem2.open();
    });
  }

  markAsSeen() {
    this.store.dispatch(ContentfulActions.markWhatsNewAsSeen());
  }

  private markAllFieldsAsTouched() {
    Object.keys(this.accountSettingForm.controls).forEach(field => {
      const control = this.accountSettingForm.get(field);

      control.markAsTouched({ onlySelf: true });
    });
  }

  private markAllNotifFieldsAsTouched() {
    Object.keys(this.notificationSettingForm.controls).forEach(field => {
      const control = this.notificationSettingForm.get(field);

      control.markAsTouched({ onlySelf: true });
    });
  }

  getUserInfo() {
    const userSubscription = this.user$.subscribe((user: AccountInfo) => {
      if (user !== null) {
        this.accountSettingForm.controls.email.setValue(
          user.idTokenClaims.email
        );
        this.accountSettingForm.controls.name.setValue(user.name);
      }
    });

    this.subscriptions.add(userSubscription);

    const userDetSubscription = this.userinfo$.subscribe(
      (user: UserDataModel) => {
        if (user !== null) {
          this.user = user;
          this.store.dispatch(GlobalActions.hideSpinner());
          // Set phone number
          this.accountSettingForm.controls.phoneNumber.setValue(
            user.phoneNumber
          );

          this.accountSettingForm.controls.isPhoneValidated.setValue(
            user.isPhoneValidated
          );

          const { notificationSettings } = user;

          this.isNotificationEnable = true;
          if (
            notificationSettings === null ||
            notificationSettings === undefined
          ) {
            this.isNotificationEnable = false;
          } else {
            this.isNotificationEnable = this.checkAnyToggleEnabled(
              notificationSettings,
              'caseUpdates'
            );
          }
          this.setNotificationSettings(notificationSettings, 'caseUpdates');
        }
      }
    );

    this.subscriptions.add(userDetSubscription);
  }

  validatePhoneNumber() {
    const phoneNumberField = this.accountSettingForm.get('phoneNumber');

    if (phoneNumberField.valid) {
      this.store.dispatch(
        TwilioActions.twilioVerify({
          requestObj: {
            channel: 'sms',
            PhoneNumberOrEmail: phoneNumberField.value,
          },
        })
      );
    }
  }

  verifyOTP() {
    const phoneNumberField = this.accountSettingForm.get('phoneNumber');
    const otpField = this.accountSettingForm.get('otp');

    if (phoneNumberField.valid && otpField.valid) {
      this.store.dispatch(
        TwilioActions.verifyOTP({
          requestObj: {
            channel: 'sms',
            PhoneNumberOrEmail: phoneNumberField.value,
            code: otpField.value,
          },
        })
      );
    }
  }

  onSubmitAccountSettings(event: Event): void {
    event.preventDefault();

    if (this.accountSettingForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    if (this.accountSettingForm.valid) {
      const { phoneNumber } = this.accountSettingForm.value;

      this.store.dispatch(
        UserDataActions.userUpdateAcccountSettingsById({
          user: {
            adObjectId: this.user.adObjectId,
            phoneNumber: phoneNumber,
            isPhoneValidated:
              phoneNumber === this.user.phoneNumber
                ? this.user.isPhoneValidated
                : false,
          },
        })
      );
    }
  }

  onSubmitNotificationSettings(): void {
    this.store.dispatch(GlobalActions.showSpinner());

    if (this.notificationSettingForm.invalid) {
      this.markAllNotifFieldsAsTouched();
      return;
    }

    const notificationSettings = this.notificationSettingForm.value;
    const newNotification: Notification = {
      caseUpdates: {
        sms: notificationSettings.sms || false,
        email: notificationSettings.email || false,
      },
    };

    this.store.dispatch(
      UserDataActions.userUpdateNotifSettingsById({
        adObjectId: this.user.adObjectId,
        notificationSettings: newNotification,
      })
    );
    this.store.dispatch(GlobalActions.hideSpinner());
  }

  checkAnyToggleEnabled(settings: Notification, type: 'caseUpdates'): boolean {
    return !!(settings && (settings[type]?.sms || settings[type]?.email));
  }

  setNotificationSettings(settings: Notification, prefix: string) {
    if (settings) {
      this.notificationSettingForm.patchValue({
        email: settings[prefix].email || false,
        sms: settings[prefix].sms || false,
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
